import { useState, forwardRef, useImperativeHandle } from 'react';
import { StatusToastItem,
	StatusToastWrapperContent,
	StatusToastWrapperContentText,
	StatusToastTextHeadline,
	StatusToastTextMessage,
	StatusToastButtonCTA,
	StyledIcon } from './StatusToast.style';
import { ToastData } from './StatusToastContext';
import { useStatusToastContext } from './StatusToastContext';
import { IconName } from '../icon';

interface StatusToastProps {
	toast: ToastData;
	onHide: () => void;
}

export interface StatusToastHandle {
	hide: () => void;
}

const ANIMATION_DURATION_MS = 300;

const STATUS_ICON_MAP: Record<NonNullable<ToastData['status']>, IconName> = {
	success: 'circle-check',
	error: 'exclamation-circle-outline',
	warning: 'warning-new',
	info: 'info-icon-new',
};

export const StatusToast = forwardRef<StatusToastHandle, StatusToastProps>(({
	toast,
	onHide,
}, ref) => {
	const [isExiting, setIsExiting] = useState(false);
	const { openOverlay } = useStatusToastContext();

	const handleHide = () => {
		setIsExiting(true);
		setTimeout(() => {
			onHide();
		}, ANIMATION_DURATION_MS);
	};

	useImperativeHandle(ref, () => ({
		hide: handleHide,
	}));

	const handleCTAClick = () => {
		if (toast.overlayTitle && toast.overlayMainText && toast.overlayItems && toast.overlayItems.length > 0) {
			openOverlay(toast.overlayTitle, toast.overlayMainText, toast.overlayItems, toast.id);
		}
	};

	const hasOverlay = toast.overlayTitle &&
		toast.overlayMainText &&
		toast.overlayItems &&
		toast.overlayItems.length > 0;

	const status = toast.status ?? 'error';

	return (
		<StatusToastItem $isExiting={isExiting}>
			<StatusToastWrapperContent>
				<StyledIcon $variant={status} icon={STATUS_ICON_MAP[status]} />
				<StatusToastWrapperContentText>
					<StatusToastTextHeadline>{toast.headline}</StatusToastTextHeadline>
					<StatusToastTextMessage>{toast.message}</StatusToastTextMessage>
					{toast.ctaText && hasOverlay && (
						<StatusToastButtonCTA
							label={toast.ctaText}
							size="small"
							variant="primary"
							onClick={handleCTAClick}
						/>
					)}
				</StatusToastWrapperContentText>
				<StyledIcon
					$variant="close"
					icon="x-slim"
					onClick={handleHide}
					style={{ cursor: 'pointer' }}
				/>
			</StatusToastWrapperContent>
		</StatusToastItem>
	);
});

StatusToast.displayName = 'StatusToast';
